import { useMutation } from "react-query";
import axios from "axios";
import { toast } from "react-hot-toast";
import { enrichCSVMapColumns } from "../apis/enrich";
import Cookies from "js-cookie";
import { useState } from "react";
import { getEnrichmentRequirementByValue } from "../axios/constant";

export const useCsvUploader = (activeWorkspace, name, enrichmentType = "People") => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const enrichCSVMapColumnsFn = useMutation(
    "enrich_columns",
    enrichCSVMapColumns, // assume you already have this imported
    {
      onSuccess(data) {
        toast.success(data.message);

        window.open(`/${process.env.REACT_APP_APP_HOME}/${activeWorkspace?.slug}/enrich/list`);
        setIsSubmitting(false);
      },
      onError() {
        setIsSubmitting(false);
      },
    }
  );

  // CSV escaping helper
  const escapeCsv = (val) => {
    if (val == null) return "";
    const str = String(val).replace(/"/g, '""');
    return str.includes(",") || str.includes('"') || str.includes("\n") ? `"${str}"` : str;
  };

  // Step 1: Convert JSON payload to CSV string
  const createCsvFile = (payload) => {
    const isCompany = enrichmentType === "Company";
    const headers = isCompany
      ? ["Company LinkedIn URL", "Company name", "Company domain"]
      : ["LinkedIn profile URL", "First name", "Last name", "Full name", "Company name", "Designation"];

    const rows = payload.map((row) =>
      isCompany
        ? [
            row.company_linkedin_url || row.linkedin_url || "",
            row.company_name || row.name || "",
            row.company_domain || row.domain || "",
          ]
        : [
            row.linkedin_url || "",
            row.first_name || "",
            row.last_name || "",
            row.full_name || "",
            row.company_name || "",
            row.designation || "",
          ]
    );

    const csvContent = [headers.join(","), ...rows.map((r) => r.map(escapeCsv).join(","))].join("\n");

    const recordLength = payload.length;

    const timestamp = Date.now();
    const normalizedName = name || "export";
    const suffix = isCompany ? "companies_salesNav" : "profiles_salesNav";
    const fileName = `${normalizedName}_${recordLength}_${suffix}_${timestamp}.csv`;

    return new File([csvContent], fileName, { type: "text/csv" });
  };

  const extractKeyIndices = (result) => {
    const keyIndices = {};
    for (const key in result) {
      if (result[key] && result[key].header_key_index !== undefined) {
        keyIndices[key] = result[key].header_key_index;
      }
    }
    return keyIndices;
  };

  // Step 2: Upload CSV
  const uploadCsv = async (payload, enrichmentMode = "Phone") => {
    setIsSubmitting(true);
    const selectedFile = createCsvFile(payload);

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("enrichment_type", enrichmentType);
    const cookieName = process.env.REACT_APP_PR_ACCESS_TOKEN
      ? process.env.REACT_APP_PR_ACCESS_TOKEN
      : "accessToken";
    const token = Cookies.get(cookieName);

    const config = {
      headers: {
        "X-Workspace-Id": activeWorkspace?.id,
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    };

    try {
      const isLegacy = ["whatfix-staging", "whatfix"].includes(activeWorkspace?.slug);
      let uploadUrl;
      if (isLegacy) {
        uploadUrl = `${process.env.REACT_APP_API_BASE_URL}/api/v1/enrich/csv/upload`;
      } else {
        uploadUrl = `${process.env.REACT_APP_NUCLEUS_API_BASE_URL}/api/v2/enrich/csv/upload`;
      }

      const response = await axios.post(
        uploadUrl,
        formData,
        config
      );

      const csvResponse = response.data.list;

      if (csvResponse && csvResponse.header && csvResponse.sample_record && csvResponse.mapping_columns) {
        const newMappingOfCSVCols = predictMapping(
          csvResponse.header,
          csvResponse.sample_record,
          csvResponse.mapping_columns
        );

        // Step 3: Fire enrichment mapping
        const payloadForMap = {
          listId: csvResponse.id,
          slug: activeWorkspace?.slug,
          data: {
            mapping: extractKeyIndices(newMappingOfCSVCols),
          },
          options: {
            headers: { "X-Workspace-Id": activeWorkspace?.id },
          },
        };

        if (csvResponse?.enrichment_type === "Company") {
          // For Company type, set requirements to empty array
          payloadForMap.data.requirements = [];
        } else {
          // For People type, add requirements
          payloadForMap.data.requirements = getEnrichmentRequirementByValue(enrichmentMode);
          payloadForMap.data.settings = {
            verify: [],
          };
        }

        enrichCSVMapColumnsFn.mutate(payloadForMap);
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("❌ CSV Upload failed:", error);
      setIsSubmitting(false);
    }
  };

  return {
    uploadCsv,
    isCSVUploadSubmitting: isSubmitting,
    isCSVUploadLoading: isSubmitting || enrichCSVMapColumnsFn.isLoading,
  };
};

// Step 4: Predict mapping function
const predictMapping = (header, sampleRecord, mappingColumns) => {
  const result = {};

  const normalize = (value = "") => value.toString().toLowerCase().replace(/[^a-z0-9]/g, "");

  for (let i = 0; i < header.length; i++) {
    const headerValue = header[i].trim();
    const normalizedHeader = normalize(headerValue);
    let headerKey = null;
    let headerKeyIndex = null;

    for (const key in mappingColumns) {
      const normalizedMapValue = normalize(mappingColumns[key]);
      const normalizedKey = normalize(key);
      if (
        normalizedMapValue.includes(normalizedHeader) ||
        normalizedHeader.includes(normalizedMapValue) ||
        normalizedKey.includes(normalizedHeader) ||
        normalizedHeader.includes(normalizedKey)
      ) {
        headerKey = key;
        headerKeyIndex = i;
        break;
      }
    }

    // Fallback for company LinkedIn URL when mapping name differs
    if (!headerKey && normalizedHeader.includes("linkedin")) {
      if ("company_linkedin_url" in mappingColumns) {
        headerKey = "company_linkedin_url";
        headerKeyIndex = i;
      } else if ("linkedin_url" in mappingColumns) {
        headerKey = "linkedin_url";
        headerKeyIndex = i;
      }
    }

    if (headerKey) {
      result[headerKey] = {
        value: sampleRecord[i],
        header_key: headerValue,
        header_key_index: headerKeyIndex,
      };
    } else {
      result[headerValue] = sampleRecord[i];
    }
  }
  return result;
};
